import type { MessageContent, ToolCall } from './types';

import ColorNamer from 'color-namer';
import type { Tool } from 'js/src/agent/tools';
import type { ConversationTool } from 'js/src/agent/tools/ConversationTool';

/**
 * Converts a 24-bit integer color (e.g., 16777215) to an RGB object.
 * @param color 24-bit integer color
 * @returns An object with r, g, b properties
 */
function intToRGB(color: number): { r: number; g: number; b: number } {
    return {
        r: (color >> 16) & 0xff,
        g: (color >> 8) & 0xff,
        b: color & 0xff,
    };
}

export function getColorName(color: number): string {
    const rgb = intToRGB(color);
    const colorName = ColorNamer(`rgb(${rgb.r},${rgb.g},${rgb.b})`).basic[0].name;
    return colorName || 'unknown';
}

export function convertJsonToYaml(obj: unknown, indentLevel: number = 0): string {
    const indent = '  '.repeat(indentLevel);

    if (obj === null) {
        return 'null';
    }

    if (obj === undefined) {
        return 'undefined';
    }

    if (typeof obj === 'boolean') {
        return obj.toString();
    }

    if (typeof obj === 'number') {
        return obj.toString();
    }

    if (typeof obj === 'string') {
        if (
            obj.includes('\n') ||
            obj.includes('"') ||
            obj.includes("'") ||
            obj.includes(':') ||
            obj.includes('-')
        ) {
            return obj.replace(/"/g, '\\"');
        }
        return obj;
    }

    if (Array.isArray(obj)) {
        if (obj.length === 0) {
            return '[]';
        }
        return obj
            .map(
                (item) =>
                    `${indent}- ${convertJsonToYaml(item, indentLevel + 1).replace(/^\s+/, '')}`,
            )
            .join('\n');
    }

    if (typeof obj === 'object' && obj !== null) {
        const keys = Object.keys(obj);
        if (keys.length === 0) {
            return '{}';
        }
        return keys
            .map((key) => {
                const value = (obj as Record<string, unknown>)[key];
                const yamlValue = convertJsonToYaml(value, indentLevel + 1);
                if (
                    typeof value === 'object' &&
                    value !== null &&
                    !Array.isArray(value) &&
                    Object.keys(value).length > 0
                ) {
                    return `${indent}${key}:\n${yamlValue}`;
                } else if (Array.isArray(value) && value.length > 0) {
                    return `${indent}${key}:\n${yamlValue}`;
                } else {
                    return `${indent}${key}: ${yamlValue}`;
                }
            })
            .join('\n');
    }

    return String(obj);
}

// Configuration constants for tracing sanitization
const MAX_CONTENT_LENGTH_PER_MESSAGE = 1000;
const MAX_TOOL_CALL_LENGTH = 200;
const MAX_TOTAL_TOOL_CALLS_LENGTH = 1000;
const BASE64_IMAGE_PATTERN = /data:image\/[^;]+;base64,([A-Za-z0-9+/=]+)/g;

/**
 * Sanitizes message content for safe inclusion in tracing spans.
 * Removes base64 image data and truncates long text content.
 */
export function sanitizeContentForTracing(content: MessageContent): string {
    if (typeof content === 'string') {
        // Simple string content - just truncate if too long
        return content.length > MAX_CONTENT_LENGTH_PER_MESSAGE
            ? content.substring(0, MAX_CONTENT_LENGTH_PER_MESSAGE) + '...[truncated]'
            : content;
    }

    // Array of TextContent and ImageContent
    const sanitizedParts: string[] = [];

    for (const part of content) {
        if (part.type === 'text') {
            const textPart = part;
            const text =
                textPart.text.length > MAX_CONTENT_LENGTH_PER_MESSAGE
                    ? textPart.text.substring(0, MAX_CONTENT_LENGTH_PER_MESSAGE) + '...[truncated]'
                    : textPart.text;
            sanitizedParts.push(text);
        } else if (part.type === 'image_url') {
            const imagePart = part;
            const imageUrl = imagePart.image_url.url;

            // Check if it's a base64 image
            const base64Match = imageUrl.match(BASE64_IMAGE_PATTERN);
            if (base64Match) {
                // Calculate approximate size of base64 data
                const base64Data = base64Match[0].split(',')[1];
                const sizeBytes = Math.floor((base64Data.length * 3) / 4);
                sanitizedParts.push(`[IMAGE:${sizeBytes} bytes]`);
            } else {
                // Regular URL - include it but truncate if very long
                const url =
                    imageUrl.length > 200
                        ? imageUrl.substring(0, 200) + '...[truncated]'
                        : imageUrl;
                sanitizedParts.push(`[IMAGE_URL:${url}]`);
            }
        }
    }

    return sanitizedParts.join(' ');
}

/**
 * Sanitizes tool calls for safe inclusion in tracing spans.
 * Strips base64 images but keeps user data for debugging.
 */
export function sanitizeToolCallsForTracing(toolCalls: ToolCall[]): string {
    const sanitizedCalls: string[] = [];
    let totalLength = 0;

    for (const toolCall of toolCalls) {
        try {
            // Parse arguments to sanitize them
            const args = JSON.parse(toolCall.function.arguments);
            const sanitizedArgs: string[] = [];

            // Process each argument
            for (const [key, value] of Object.entries(args)) {
                let sanitizedValue: string;

                if (typeof value === 'string') {
                    // Check for base64 images and replace them
                    if (value.match(BASE64_IMAGE_PATTERN)) {
                        const base64Data = value.split(',')[1] || '';
                        const sizeBytes = Math.floor((base64Data.length * 3) / 4);
                        sanitizedValue = `[IMAGE:${sizeBytes} bytes]`;
                    } else {
                        // Keep string values but truncate if very long
                        sanitizedValue =
                            value.length > 50 ? `"${value.substring(0, 50)}..."` : `"${value}"`;
                    }
                } else if (typeof value === 'number' || typeof value === 'boolean') {
                    sanitizedValue = String(value);
                } else if (Array.isArray(value)) {
                    sanitizedValue = `[${value.length} items]`;
                } else if (typeof value === 'object' && value !== null) {
                    sanitizedValue = `{${Object.keys(value).length} props}`;
                } else {
                    sanitizedValue = String(value);
                }

                sanitizedArgs.push(`${key}=${sanitizedValue}`);
            }

            // Format as function call
            const formattedCall = `${toolCall.function.name}(${sanitizedArgs.join(', ')})`;

            // Truncate individual tool call if too long
            const finalCall =
                formattedCall.length > MAX_TOOL_CALL_LENGTH
                    ? formattedCall.substring(0, MAX_TOOL_CALL_LENGTH) + '...'
                    : formattedCall;

            // Check if adding this call would exceed total length limit
            if (totalLength + finalCall.length > MAX_TOTAL_TOOL_CALLS_LENGTH) {
                sanitizedCalls.push('[...additional tool calls truncated]');
                break;
            }

            sanitizedCalls.push(finalCall);
            totalLength += finalCall.length;
        } catch (error) {
            // If JSON parsing fails, just include the tool name
            const fallback = `${toolCall.function.name}([invalid args])`;
            sanitizedCalls.push(fallback);
            totalLength += fallback.length;
        }
    }

    return sanitizedCalls.join(' | ');
}

export function isConversationTool(tool: Tool): tool is ConversationTool {
    return (
        'attachToConversation' in tool &&
        typeof (tool as unknown as { attachToConversation: unknown }).attachToConversation ===
            'function'
    );
}
